import React, { FC, useMemo } from "react";

import useWatchState from "./../../../../hooks/useWatchState";
import useImageLoadDone from "./hooks/useImageLoad";
import useImageSizes from "./hooks/useImageSizes";

import { IImagePlaceholder } from "./Image.interface";
import { LOAD_PLACEHOLDER_IMAGE } from "./Image.context";

const IMAGE_SIZES_REG_EXP = /[_-]?\d{2,4}x\d{2,4}/;

export const ImagePlaceholder: FC<IImagePlaceholder> = ({
  children,
  src,
  placeholderUrl,
  hasEnabled = true,
  maxHeight = "700px",
  styles = {},
}) => {
  const hasLoaded = useImageLoadDone(src);
  const [width, height = ""] = useMemo(() => {
    const [imgExtractedSize] = IMAGE_SIZES_REG_EXP.exec(src) ?? [``];
    const sizeString: string = imgExtractedSize.startsWith(`-`)
      ? imgExtractedSize.slice(1)
      : imgExtractedSize;

    return sizeString.split(`x`);
  }, [src]);
  const memoizedSizes = useMemo(() => ({ width, height }), [width, height]);
  const [imageSizes, setImageSizes] = useWatchState({ value: memoizedSizes });

  useImageSizes({
    ...imageSizes,
    setSizes: setImageSizes,
    src,
  });

  return !hasEnabled || hasLoaded ? (
    <>{children}</>
  ) : (
    <img
      loading="lazy"
      decoding="async"
      width={imageSizes.width}
      height={imageSizes.height}
      src={placeholderUrl ?? LOAD_PLACEHOLDER_IMAGE}
      style={{
        color: `transparent`,
        width: `100%`,
        height: `auto`,
        maxHeight,
        backgroundColor: `var(--bs-gray-100)`,
        backgroundSize: `cover`,
        backgroundPosition: `50% 50%`,
        backgroundRepeat: `no-repeat`,
      }}
      {...styles}
    />
  );
};

export default ImagePlaceholder;
